import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  BookOpen,
  ChartNoAxesCombined,
  ChevronDown,
  Crown,
  HomeIcon,
  LogOut,
  Menu,
  NotebookPen,
  Timer,
  X,
  UserCircle,
  ChatCircle,
  Question,
} from './Icons';
import { type Membership } from '@mastery/api-client-web';
import { useAuth, useOptionalSession } from '../lib/session';
import { authURL, referralCode } from '../lib/navigation';
import { PageBoundary } from './PageState';
import { date, useLoad } from '../lib/shared';
import { Brand } from './Brand';
import { updateWechatShare } from '../lib/wechat-share';

const navigation = [
  ['/', '首页', HomeIcon],
  ['/practice', '题库与专项', BookOpen],
  ['/mock', '模拟测试', Timer],
  ['/wrong', '错题本', NotebookPen],
  ['/progress', '能力分析', ChartNoAxesCombined],
  ['/history', '答题历史', BookOpen],
  ['/membership', '进阶会员', Crown],
  ['/account', '我的账号', UserCircle],
  ['/help/requests', '我的问题', ChatCircle],
  ['/help', '帮助中心', Question],
] as const;
const primaryNavigation = [navigation[0]] as const;
const allNavigation = [...primaryNavigation, ...navigation.slice(3)] as const;

export function Layout({ logout }: { logout: () => void }) {
  const user = useOptionalSession()?.user;
  const { requestLogin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { pathname: routePathname } = location;
  const pathname = routePathname.replace(/\/+$/, '') || '/';
  const incomingFlash = (location.state as { flashNotice?: string } | null)?.flashNotice ?? '';
  const [flashNotice, setFlashNotice] = useState('');
  const [open, setOpen] = useState(false);
  const [personal, setPersonal] = useState(false);
  const menu = useRef<HTMLDialogElement>(null);
  const menuTrigger = useRef<HTMLButtonElement>(null);
  const account = useRef<HTMLDivElement>(null);
  const accountTrigger = useRef<HTMLButtonElement>(null);
  function requestLogout() {
    (open ? menuTrigger.current : accountTrigger.current)?.focus({ preventScroll: true });
    setOpen(false);
    setPersonal(false);
    logout();
  }
  const journalNav = useRef<HTMLElement>(null);
  const membership = useLoad<Membership>(user ? '/membership' : null);
  const isAttempt = pathname.startsWith('/attempts/');
  const isBankRoute =
    pathname === '/' ||
    /^\/(?:KET|PET|FCE|CAE)(?:\/|$)/u.test(pathname) ||
    pathname.startsWith('/bank-preview');
  const isPersonal =
    !!user &&
    ['/wrong', '/progress', '/history', '/membership', '/account'].some((p) =>
      pathname.startsWith(p),
    );
  const currentPage =
    pathname === '/'
      ? '冰激凌英语营'
      : isBankRoute
        ? '题库'
        : pathname === '/learning'
          ? '学习中心'
          : pathname === '/confirm-email'
            ? '确认登录邮箱'
            : pathname.startsWith('/help/requests')
              ? '我的问题'
              : isAttempt
                ? '练习与解析'
                : (navigation.find(([to]) => to === pathname)?.[1] ?? '页面未找到');
  const registerURL = authURL('register', '/', referralCode());
  const tier = !membership.data
    ? '学习权益'
    : membership.data.tier === 'member'
      ? '进阶会员'
      : membership.data.tier === 'trial'
        ? '体验会员'
        : membership.data.tier === 'staff'
          ? '团队账号'
          : '查看学习权益';
  useEffect(() => {
    window.scrollTo({ top: 0 });
    setOpen(false);
    setPersonal(false);
    document.title =
      currentPage === '冰激凌英语营'
        ? '冰激凌英语营 · Mastery+'
        : currentPage + ' · 冰激凌英语营 · Mastery+';
    const nav = journalNav.current;
    const active = nav?.querySelector<HTMLElement>('a.active');
    if (nav && active && nav.scrollWidth > nav.clientWidth)
      nav.scrollLeft = active.offsetLeft - (nav.clientWidth - active.clientWidth) / 2;
  }, [pathname, currentPage]);
  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      updateWechatShare({
        title: document.title,
        description: `${currentPage} · 冰激凌英语营，按自己的节奏练习并查看解析。`,
        link: window.location.href,
        image: `${window.location.origin}/logo.png`,
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [currentPage, location.pathname, location.search]);
  useEffect(() => {
    if (!incomingFlash) return;
    setFlashNotice(incomingFlash);
    const state =
      location.state && typeof location.state === 'object'
        ? { ...(location.state as Record<string, unknown>) }
        : {};
    delete state.flashNotice;
    navigate(location.pathname + location.search + location.hash, {
      replace: true,
      state: Object.keys(state).length ? state : undefined,
    });
    const timer = window.setTimeout(() => setFlashNotice(''), 6000);
    return () => window.clearTimeout(timer);
  }, [incomingFlash, location.hash, location.pathname, location.search, navigate]);
  useEffect(() => {
    if (!personal) return;
    const outside = (e: PointerEvent) => {
      if (!account.current?.contains(e.target as Node)) setPersonal(false);
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setPersonal(false);
        accountTrigger.current?.focus();
      }
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [personal]);
  useEffect(() => {
    if (!open) return;
    const dialog = menu.current!;
    dialog.showModal();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const media = window.matchMedia('(min-width: 901px)');
    const closeOnDesktop = () => {
      if (media.matches) setOpen(false);
    };
    media.addEventListener('change', closeOnDesktop);
    return () => {
      dialog.close();
      document.body.style.overflow = overflow;
      media.removeEventListener('change', closeOnDesktop);
      menuTrigger.current?.focus({ preventScroll: true });
    };
  }, [open]);
  return (
    <div className={'shell' + (isAttempt ? ' learning-shell' : '')}>
      <a className="skip-link" href="#main-content">
        跳至主要内容
      </a>
      <header className="site-header">
        <div className="site-header-inner">
          <Brand />
          <nav className="desktop-nav" aria-label="学习导航">
            {primaryNavigation.map(([to, title]) => (
              <NavLink
                to={to}
                end={to === '/'}
                key={to}
              >
                {title}
              </NavLink>
            ))}
            {user && (
              <NavLink to="/progress" className={pathname.startsWith('/progress') ? 'active' : ''}>
                能力分析
              </NavLink>
            )}
            <NavLink
              to="/history"
              className={pathname.startsWith('/history') ? 'active' : ''}
              onClick={(event) => {
                if (!user) {
                  event.preventDefault();
                  requestLogin({ reason: 'manual' });
                }
              }}
            >
              答题历史
            </NavLink>
          </nav>
          <div className="header-actions">
            <Link className="member-link" to="/membership">
              <Crown size={19} weight="duotone" />
              进阶会员
            </Link>
            {user ? (
              <div
                className="account-menu"
                ref={account}
                onBlur={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) setPersonal(false);
                }}
              >
                <button
                  className="account-trigger"
                  ref={accountTrigger}
                  aria-label="打开我的账号"
                  aria-expanded={personal}
                  aria-controls="account-links"
                  onClick={() => setPersonal(!personal)}
                >
                  <span className="avatar">{user.name.slice(0, 1)}</span>
                  <ChevronDown size={15} />
                </button>
                {personal && (
                  <div className="account-popover" id="account-links">
                    <div className="account-greeting">
                      <strong>{user.name}</strong>
                      <small>{tier}</small>
                    </div>
                    <nav aria-label="我的账号">
                      {navigation.slice(3).map(([to, title, Icon]) => (
                        <Link to={to} key={to}>
                          <Icon size={19} weight="duotone" />
                          {title}
                          <ArrowRight size={15} />
                        </Link>
                      ))}
                    </nav>
                    <button className="logout-button" onClick={requestLogout}>
                      <LogOut size={19} />
                      退出登录
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="visitor-account">
                <button
                  className="text-button"
                  type="button"
                  onClick={() => requestLogin({ reason: 'manual' })}
                >
                  登录
                </button>
                <Link className="button primary" to={registerURL}>
                  免费体验 <ArrowRight size={17} />
                </Link>
              </div>
            )}
            <button
              className="icon-button mobile-menu"
              ref={menuTrigger}
              aria-label="打开菜单"
              aria-haspopup="dialog"
              aria-expanded={open}
              onClick={() => setOpen(true)}
            >
              <Menu size={26} />
            </button>
          </div>
        </div>
      </header>
      {open && (
        <dialog
          className="navigation-dialog"
          ref={menu}
          onCancel={() => setOpen(false)}
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
          aria-label="学习导航菜单"
        >
          <div className="navigation-sheet">
            <div className="mobile-nav-brand">
              <Brand compact />
              <button
                className="icon-button"
                aria-label="关闭菜单"
                onClick={() => setOpen(false)}
                autoFocus
              >
                <X size={24} />
              </button>
            </div>
            <p className="eyebrow">A LITTLE BETTER, EVERY DAY</p>
            <nav className="mobile-nav" aria-label="移动端学习导航">
              {(user
                ? allNavigation
                : allNavigation.filter(([to]) =>
                    [
                      '/',
                      '/learning',
                      '/practice',
                      '/mock',
                      '/membership',
                      '/history',
                      '/help',
                    ].includes(to),
                  )
              ).map(([to, title, Icon], i) => (
                <NavLink
                  to={to}
                  end={to === '/'}
                  key={to}
                  onClick={(event) => {
                    if (to === '/history' && !user) {
                      event.preventDefault();
                      setOpen(false);
                      requestLogin({ reason: 'manual' });
                    } else setOpen(false);
                  }}
                >
                  <span className="mobile-nav-number" aria-hidden="true">
                    0{i + 1}
                  </span>
                  <Icon size={23} weight="duotone" aria-hidden="true" />
                  {title}
                  <ArrowRight size={20} />
                </NavLink>
              ))}
            </nav>
            {user ? (
              <div className="mobile-account">
                <strong>{user.name}</strong>
                <p>
                  {tier}
                  {membership.data?.active && ' · ' + date(membership.data.expires_at) + ' 到期'}
                </p>
                <button className="text-button" onClick={requestLogout}>
                  <LogOut size={18} />
                  退出登录
                </button>
              </div>
            ) : (
              <div className="mobile-account visitor-mobile-account">
                <p>先选一组感兴趣的内容，开启你的学习手记。</p>
                <Link className="button primary" to={registerURL} onClick={() => setOpen(false)}>
                  开启免费体验 <ArrowRight size={18} />
                </Link>
                <button
                  className="text-button"
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    requestLogin({ reason: 'manual' });
                  }}
                >
                  已有账号，去登录
                </button>
              </div>
            )}
          </div>
        </dialog>
      )}
      <main className="content" id="main-content" tabIndex={-1}>
        {flashNotice && (
          <div className="connection-status" role="status">
            <span>{flashNotice}</span>
            <button className="subtle" onClick={() => setFlashNotice('')}>
              知道了
            </button>
          </div>
        )}
        {isPersonal && (
          <nav className="journal-nav" aria-label="我的学习栏目" ref={journalNav}>
            {navigation.slice(3, 8).map(([to, title, Icon]) => (
              <NavLink to={to} key={to}>
                <Icon size={19} weight="duotone" />
                {title}
              </NavLink>
            ))}
          </nav>
        )}
        <PageBoundary>
          <Outlet />
        </PageBoundary>
      </main>
      <footer className="site-footer">
        <div>
          <Brand compact />
          <p>找准弱项，让每一次练习都有方向。</p>
        </div>
        <div className="footer-links">
          <Link to="/help">
            帮助中心 <Question size={17} />
          </Link>
          <Link to="/practice">
            开始学习
            <ArrowRight size={16} />
          </Link>
          <span>冰激凌英语营 · Know yourself. Practise smarter.</span>
        </div>
      </footer>
    </div>
  );
}
